import { RenderingWarning } from 'components/Collection/RenderingWarning';
import { ArrowLeft, ArrowRight } from 'components/Icons/Icons';
import React, { useEffect, useRef } from 'react';
import styles from './MobileCarousel.module.scss';
import { AnimatePresence, m, useAnimation } from 'framer-motion';
import { GribblePreviewCard } from './PreviewCard';
import image from './gribble-placeholder.png';
import { CarouselProps, GribbleProperties, PositionDots, RevealHeader } from './Summary';
import cls from 'classnames';
import { Button } from 'components/Button/Button';
import { DropdownAnchor } from 'components/Dropdown/DropdownAnchor';
import { GribbleSharing } from 'components/Account/GribbleCollection';
import { GRIBBLE_CONTRACT_ADDRESS } from 'utils/contract';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';

export const MobileCarousel = ({
  currentGribble,
  gribbleCount,
  index,
  first,
  last,
  next,
  prev,
  stage,
  revealAll,
  currentStageRevealed,
  revealOne,
  finish,
}: CarouselProps) => {
  const animation = useAnimation();
  useEffect(() => {
    animation.start({ x: 0 });
  }, [animation]);
  const ref = useRef(0);
  const hasMultipleGribbles = gribbleCount > 1;
  const isRevealing = stage === 'colors' || stage === 'properties';

  return (
    <div className={styles.carouselContainer}>
      <AnimatePresence exitBeforeEnter>
        {/* TODO: fix exit animation. */}
        <m.div
          className={styles.carousel}
          initial={{ x: '100vw' }}
          animate={{ x: 0 }}
          transition={{ duration: 0.5 }}
          key={ref.current}
        >
          {!isRevealing && (
            <>
              <div
                className={cls(styles.gribbleCardContainer, {
                  [styles.hasArrows]: hasMultipleGribbles,
                })}
              >
                {hasMultipleGribbles && (
                  <button
                    role="button"
                    className={styles.arrow}
                    onClick={() => {
                      if (first) {
                        return;
                      }
                      prev();
                      ref.current++;
                    }}
                  >
                    <ArrowLeft stroke={first ? 'var(--white-60)' : 'white'} />
                  </button>
                )}
                <GribblePreviewCard id={currentGribble.id} image={image} />
                {hasMultipleGribbles && (
                  <button
                    role="button"
                    onClick={() => {
                      if (last) {
                        return;
                      }
                      next();
                      // currently, required for animation
                      ref.current++;
                    }}
                    className={styles.arrow}
                  >
                    <ArrowRight stroke={last ? 'var(--white-60)' : 'white'} />
                  </button>
                )}
              </div>
              <div className={styles.positionDots}>
                <PositionDots index={index} gribbleCount={gribbleCount} />
              </div>
              <RenderingWarning hatchStartedAt={currentGribble.hatchStartedAt} />
            </>
          )}
          {isRevealing && <RevealHeader stage={stage} />}
          <div className={styles.propertiesWrapper}>
            {!isRevealing && <p className={'text-uppercase text-60'}>Properties</p>}
            <div
              className={cls(styles.properties, {
                [styles.revealing]: isRevealing,
              })}
            >
              <GribbleProperties
                small
                isRevealing={isRevealing}
                currentGribble={currentGribble}
                stage={stage}
                revealOne={revealOne}
              />
            </div>
            <HorizontalDivider />
          </div>
          <div className={cls(styles.buttons, { [styles.hasSharing]: last || gribbleCount > 1 })}>
            {(last || gribbleCount > 1) && (
              <>
                <DropdownAnchor>
                  <GribbleSharing
                    gribble={{
                      nftId: Number(currentGribble.id),
                      contract: GRIBBLE_CONTRACT_ADDRESS,
                    }}
                  ></GribbleSharing>
                </DropdownAnchor>
                <div className={styles.verticalDivider}></div>
              </>
            )}
            {gribbleCount > 1 && (
              <Button small onClick={prev} disabled={first}>
                Previous
              </Button>
            )}
            <Button
              small
              fullWidth
              primary
              onClick={last ? finish : next}
              disabled={!currentStageRevealed && !last && gribbleCount === 1}
            >
              {last ? 'Finish' : 'Next'}
            </Button>
            {isRevealing && (
              <Button
                small
                fullWidth
                onClick={revealAll}
                className={styles.revealAllButton}
                disabled={currentStageRevealed}
              >
                Reveal All
              </Button>
            )}
          </div>
        </m.div>
      </AnimatePresence>
    </div>
  );
};
